import React, { useEffect, useState, useContext, Fragment } from "react";
import { useNavigate } from "react-router-dom";
import Grid from "@mui/material/Unstable_Grid2";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import SearchIcon from "@mui/icons-material/Search";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import { DisplayPolicy } from "./components/DisplayPolicy";
import { PolicyImport } from "./components/PolicyImport";
import CSDataTable from "../../components/CSDataTable";
import { CSDataTableColumns } from "../../modal/CSDataTable";
import { convertToDateMonthFormat } from "../../utils/ConvertDate";
import { fetchPolicyList } from "./helper";
import PolicyContext from "../../context/policy/PolicyContext";
import { exportExcel } from "./utils";
import { deletePolicy, downloadAllPolicy } from "./helper";
import DialogBoxComponent from "../../components/DialogBoxComponent";
import { Stack, TextField } from "@mui/material";
import { useUserContext } from "../../context/auth/UserContext";
import { Box, CircularProgress } from "@mui/material";
import { useAccessRights } from "../../utils/useAccessRights";
import { AccessRight } from "../../modal/UserAccess";

export const Policies = () => {
  const navigate = useNavigate();
  const policyPageAccess = useAccessRights("Policy Page");
  console.log("policyPageAccess IND", policyPageAccess);

  const [policyList, setPolicyList] = useState<any[]>([]);
  const [filterList, setFilterList] = useState<any[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [previewRecord, setPreviewRecord] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [openPolicyImport, setOpenPolicyImport] = useState(false);
  const [dialogState, setDialogState] = useState({
    title: "",
    body: "",
    action: "",
    actionCancel: "",
    openDeleteConfirmationDialog: false,
    openSuccessDialogBox: false,
  });
  const { userId, companyProfileID } = useUserContext();
  const policyContext = useContext(PolicyContext);
  const {
    selectedPolicy,
    setSelectedPolicy,
    previewPolicy,
    setPreviewPolicyRecord,
  } = policyContext;

  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const displayAddPolicy = () => {
    setSelectedPolicy && setSelectedPolicy([]);
    setPreviewPolicyRecord && setPreviewPolicyRecord({});
    navigate("/add-policy");
  };

  const displayImportPolicy = () => {
    setOpenPolicyImport(true);
  };

  const handlePolicyImportCancel = () => {
    setOpenPolicyImport(false);
  };

  const handleCloseDialog = () => {
    if (dialogState.title !== "Error") {
      fetchTableData();
      setDialogState({
        ...dialogState,
        openDeleteConfirmationDialog: false,
        openSuccessDialogBox: false,
      });
    } else {
      setDialogState({
        ...dialogState,
        openDeleteConfirmationDialog: false,
        openSuccessDialogBox: false,
      });
    }
  };

  const handleDeletePolicy = async () => {
    setPreviewPolicyRecord && setPreviewPolicyRecord({});
    setDialogState(() => ({
      ...dialogState,
      openDeleteConfirmationDialog: false,
      openSuccessDialogBox: false,
    }));

    const policyID = selectedPolicy.map((ele) => ele.policyID);

    const date = new Date();
    const payload = {
      policyID: policyID,
      isDeleted: true,
      updateBy: userId,
      updateDate: date,
    };
    const res = await deletePolicy(payload);
    // console.log("handleDeletePolicy res", payload, res);
    if (res.succeeded === true) {
      setSelectedPolicy && setSelectedPolicy([]);
      setDialogState({
        title: "Success",
        body: "Policy successfully deleted",
        action: "OK",
        actionCancel: "",
        openDeleteConfirmationDialog: false,
        openSuccessDialogBox: true,
      });
    } else {
      setDialogState({
        title: "Error",
        body: "Unexpected Error",
        action: "OK",
        actionCancel: "",
        openDeleteConfirmationDialog: false,
        openSuccessDialogBox: true,
      });
    }
  };

  const confirmDelete = () => {
    handleClose();
    if (selectedPolicy.length > 0) {
      const deletePolicies = selectedPolicy
        .map((ele) => `<li>${ele.policyName}</li>`)
        .join("");
      const msg = `Are you sure you want to delete?<br/><ul><strong>${deletePolicies}</strong></ul>`;
      setDialogState({
        title: "Confirmation",
        body: msg,
        action: "Delete",
        actionCancel: "Cancel",
        openDeleteConfirmationDialog: true,
        openSuccessDialogBox: false,
      });
    } else {
      setDialogState({
        title: "Info",
        body: "Please select policy to be deleted",
        action: "OK",
        actionCancel: "",
        openDeleteConfirmationDialog: false,
        openSuccessDialogBox: true,
      });
    }
  };

  const handleDownloadAll = () => {
    const res = downloadAllPolicy(companyProfileID);
  };

  const fetchTableData = async (refreshPreview?: boolean) => {
    try {
      setIsLoading(true);
      const res = await fetchPolicyList(companyProfileID);
      const list = res.map((ele: any) => ({
        ...ele,
        rowId: ele.policyID,
        approvedDate: convertToDateMonthFormat(ele.policyApprovalDate),
        createdDate: convertToDateMonthFormat(ele.createDate),
      }));
      setPolicyList(list);
      setFilterList(list);
      if (Object.keys(previewPolicy).length > 0 && !refreshPreview) {
        setPreviewRecord(previewPolicy);
      } else {
        setPreviewRecord(list[0]);
      }
      setIsLoading(false);
    } catch (error) {
      // console.log("@@Error:", error);
    }
  };

  useEffect(() => {
    fetchTableData();
  }, []);

  const columns: CSDataTableColumns[] = [
    {
      accessorKey: "policyName",
      header: "Name",
      size: "150px",
      tooltip: true,
    },
    {
      accessorKey: "policyVersion",
      header: "Version",
      size: "60px",
    },
    {
      accessorKey: "policyStatus",
      header: "Status",
      size: "80px",
    },
    {
      accessorKey: "createdDate",
      header: "Created Date",
      size: "80px",
    },
    {
      accessorKey: "approvedDate",
      header: "Approved Date",
      size: "80px",
    },
  ];

  const handleSelectPreview = (selectedRecord: any) => {
    setPreviewPolicyRecord && setPreviewPolicyRecord(selectedRecord);
  };

  const handleSelectRow = (selectedRows: any) => {
    setSelectedPolicy && setSelectedPolicy(selectedRows);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const searchQuery = event.target.value;
    setSearchQuery(searchQuery);

    const filteredPolicies = filterList.filter((policy) =>
      policy.policyName.toLowerCase().includes(searchQuery.toLowerCase())
    );

    setPolicyList(filteredPolicies);
  };

  return (
    <Fragment>
      <Grid container sx={{ height: "100%", width: "100%" }}>
        <Grid
          xs={6}
          style={{
            padding: "1rem",
            margin: "1rem 0 1.5rem 0",
            background: "#FFF",
            borderRadius: "2%",
          }}
        >
          <Grid
            container
            display="flex"
            flexDirection="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <h3 style={{ fontWeight: "400" }}>
              All Policies ({policyList.length})
            </h3>

            <Stack direction="row">
              <Button
                size="small"
                variant="contained"
                className="table-btn"
                onClick={displayAddPolicy}
                disabled={!policyPageAccess?.isCreate || isLoading}
              >
                Create New
              </Button>
              <Button
                size="small"
                variant="contained"
                className="table-btn"
                onClick={displayImportPolicy}
                disabled={!policyPageAccess?.isImport || isLoading}
              >
                Import Policies
              </Button>
            </Stack>

            <TextField
              sx={{ width: "9rem" }}
              size="small"
              variant="outlined"
              placeholder="Search Policies"
              value={searchQuery}
              disabled={isLoading}
              onChange={handleSearchChange}
            />
            <IconButton
              aria-label="menu"
              disabled={isLoading}
              onClick={handleClick}
            >
              <MoreVertIcon />
            </IconButton>
            <Menu
              id="basic-menu"
              anchorEl={anchorEl}
              open={open}
              onClose={handleClose}
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "right",
              }}
              transformOrigin={{
                vertical: "top",
                horizontal: "right",
              }}
            >
              <MenuItem
                disabled={!policyPageAccess?.isDownload}
                onClick={handleDownloadAll}
              >
                Download All
              </MenuItem>

              <MenuItem onClick={() => exportExcel(policyList)}>
                Download Spreadsheet
              </MenuItem>
              <MenuItem
                disabled={!policyPageAccess?.isDelete}
                onClick={confirmDelete}
              >
                Delete
              </MenuItem>
            </Menu>
          </Grid>
          {isLoading ? (
            <Box display="flex" justifyContent="center">
              <CircularProgress />
            </Box>
          ) : (
            <CSDataTable
              rows={policyList}
              columns={columns}
              multiRowSelection={true}
              previewRecord={previewRecord}
              handleSelectPreview={(selectedRecord) =>
                handleSelectPreview(selectedRecord)
              }
              handleSelectRow={(selectedRows) => handleSelectRow(selectedRows)}
            />
          )}
        </Grid>

        <Grid
          xs
          style={{
            padding: "1rem",
            margin: "1rem",
            background: "#FFF",
            borderRadius: "2%",
          }}
        >
          <DisplayPolicy
            refreshList={() => fetchTableData(true)}
            policyPageAccess={policyPageAccess}
          />
        </Grid>
      </Grid>
      {
        <PolicyImport
          openDialog={openPolicyImport}
          handleCancel={handlePolicyImportCancel}
          refreshList={() => fetchTableData()}
          policyList={policyList}
        />
      }
      {
        <DialogBoxComponent
          openDialogBox={dialogState.openSuccessDialogBox}
          dialogTitle={dialogState.title}
          dialogAction={dialogState.action}
          dialogBody={dialogState.body}
          handleFunction={handleCloseDialog}
        />
      }
      {
        <DialogBoxComponent
          openDialogBox={dialogState.openDeleteConfirmationDialog}
          dialogTitle={dialogState.title}
          dialogAction={dialogState.action}
          dialogActionCancel={dialogState.actionCancel}
          dialogBody={dialogState.body}
          handleFunction={handleDeletePolicy}
          handleCancel={handleCloseDialog}
        />
      }
    </Fragment>
  );
};
